import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/ui/Footer';
import { Award, Users, Target, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';
import { ExpandableTestimonial } from '../components/ui/ExpandableTestimonial';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.8,
    ease: [0.22, 1, 0.36, 1]
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
export function AboutUsPage() {
  return <div className="bg-black min-h-screen" data-id="element-218">
      <Header data-id="element-219" />
      <main className="pt-32 pb-24 px-6" data-id="element-220">
        <div className="max-w-7xl mx-auto" data-id="element-221">
          <motion.div className="mb-16" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-222">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6 font-roboto-condensed" data-id="element-223">
              About Us
            </h1>
            <p className="text-xl text-white/80 max-w-3xl leading-relaxed" data-id="element-224">
              We are enterprise data strategists who deliver premium insights at
              scale, trusted by healthcare leaders who value speed, precision,
              and successful outcomes.
            </p>
          </motion.div>
          <motion.div className="grid lg:grid-cols-2 gap-12 mb-16" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-225">
            <motion.div variants={fadeInUp} data-id="element-226">
              <h2 className="text-3xl font-semibold text-white mb-6 font-roboto-condensed" data-id="element-227">
                Our Mission
              </h2>
              <p className="text-white/70 leading-relaxed mb-4" data-id="element-228">
                We exist to transform healthcare data chaos into strategic
                clarity. Our specialized focus on U.S. healthcare data
                management ensures that organizations can make confident,
                data-driven decisions that improve patient care and operational
                efficiency.
              </p>
              <p className="text-white/70 leading-relaxed" data-id="element-229">
                With deep expertise in Cloud Data Strategy, Master & Reference
                Data Management (MDM/RDM), Enterprise Data Governance, Data
                Cataloging, and Data Stewardship programs, we deliver measurable
                results that drive long-term success.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300" whileHover={{
            y: -8
          }} data-id="element-230">
              <h3 className="text-2xl font-semibold text-white mb-6 font-roboto-condensed" data-id="element-231">
                What Sets Us Apart
              </h3>
              <ul className="space-y-4" data-id="element-232">
                {[{
                icon: Award,
                title: 'Enterprise Scale Expertise',
                desc: 'Trusted by C-suite and senior leaders for high-stakes engagements'
              }, {
                icon: Target,
                title: 'Healthcare Specialization',
                desc: 'Deep understanding of U.S. healthcare data ecosystems'
              }, {
                icon: Users,
                title: 'Proven Track Record',
                desc: 'Clients return for multiple phases due to measurable success'
              }, {
                icon: Lightbulb,
                title: 'Platform Expertise',
                desc: 'Deep knowledge of leading MDM and governance platforms'
              }].map((item, index) => <motion.li key={index} className="flex items-start gap-3" initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.1 * index,
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-233">
                    <item.icon className={`w-6 h-6 ${index % 2 === 0 ? 'text-purple-400' : 'text-cyan-400'} flex-shrink-0 mt-1`} data-id="element-234" />
                    <div data-id="element-235">
                      <h4 className="text-white font-semibold mb-1" data-id="element-236">
                        {item.title}
                      </h4>
                      <p className="text-white/70 text-sm" data-id="element-237">{item.desc}</p>
                    </div>
                  </motion.li>)}
              </ul>
            </motion.div>
          </motion.div>

          <motion.div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 lg:p-12" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-238">
            <h2 className="text-3xl font-semibold text-white mb-8 text-center font-roboto-condensed" data-id="element-239">
              Client Testimonials
            </h2>
            <motion.div className="space-y-8 max-w-4xl mx-auto" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
            once: true,
            margin: '-100px'
          }} data-id="element-240">
              {[{
              quote: 'Brigitte is undoubtedly one of the most talented, diligent and driven colleagues I have worked with. Her expert knowledge of data management and healthcare domain knowledge combined with excellent team building and people skills make her an incredible partner on any complex project. Brigitte’s work ethic, warmth and humor make collaborating with her a pleasure. I would welcome any opportunity to work with her again and would recommend her highly on any team.',
              author: 'Madhavi Bharadwaj',
              role: 'Healthcare and Analytics | Former Presidential Innovation Fellow (HHS-OIG)',
              initials: 'MB'
            }, {
              quote: 'I’ve had the privilege of working with Brigitte and can confidently say she brings a rare combination of deep expertise and practical execution to one of healthcare’s most complex challenges—provider data. Brigitte doesn’t just understand data governance in theory—she understands how to operationalize it. She has a unique ability to bridge the gap between fragmented data environments and real-world clinical and operational needs, helping organizations move toward a true, reliable source of truth. Her work in structuring and managing provider data—especially across complex systems and provider directory environments—is both strategic and highly actionable. What sets her apart is her clarity of thought and her ability to translate complexity into systems that actually work. She sees the full lifecycle of provider data and understands how to align it with credentialing, operations, and access in a way that drives measurable impact. If the goal is to modernize how healthcare manages and leverages provider data, Brigitte is exactly the kind of leader you want at the table.',
              author: 'Victor Vargas, MD, MBA, CPE',
              role: 'Chief Medical Officer | Physician Executive | Medical Staff Governance & Operational Leadership',
              initials: 'VV'
            }, {
              quote: 'Brigitte Tebow is my latest favorite follow. She articulates with far more specificity and real world experience than myself the lack of preparedness of health plans and systems for the demands of FHIR API data exchange and operations. As with AI, it is the data management that drives feasibility in the digital transformation space, but most organizations wrongly presume that their vendors will “work it out.”',
              author: 'Michael E Campbell',
              role: 'Healthcare Informatics Strategist and Consultant',
              initials: 'MC'
            }, {
              quote: 'There aren’t many people on the planet who know MDM as well as Brigitte Tebow, especially in the healthcare space. I would trust her guidance implicitly.',
              author: 'Malcolm Hawker',
              role: 'CDO | Author | Keynote Speaker | Podcast Host',
              initials: 'MH'
            }, {
              quote: 'I had the pleasure of working with Brigitte during my job search, and her support made a real difference. She helped me set up and optimize my LinkedIn profile, guided me through the process step by step, and provided valuable insights on how to present myself professionally. She also helped me re-write my resume, making it significantly stronger and more impactful.',
              author: 'Rakshitha M.',
              role: 'Business & Data Analyst | SQL • Python • Power BI | Financial Services, Healthcare & Analytics | Stakeholder Management • Reporting • Process Improvement',
              initials: 'RM'
            }, {
              quote: 'I had the pleasure of working closely with Brigitte Workman as separate third-party consultants for the same client. Our task was to migrate legacy systems to a snowflake data warehouse model and implement an enterprise-wide data warehouse for a large regional healthcare organization while simultaneously creating necessary data governance documentation for internal use.',
              author: 'Alex Robinson',
              role: 'Data Analyst',
              initials: 'AR'
            }, {
              quote: 'As a leader in Kaiser Permanente’s Chief Data Office (CDO), I was responsible for the Washington regions Data Governance program, and integration with the National Program. I have had the pleasure and privilege to work with Brigitte Workman in this role and I highly recommend Brigitte as a skilled and strong enterprise data governance and business leader. She consistently demonstrated excellence on the CDO’s highest priority project, Analytics 2.0, through the depth and breadth of her business operations, stewardship, collaboration, project and staff management expertise.',
              author: 'Jack Lopez',
              role: 'Retired from UW Medicine IT Data Analytics',
              initials: 'JL'
            }, {
              quote: 'Brigitte is an incredibly high performer. She has excellent drive, delivers high quality products quickly, is detail oriented and has strong healthcare and data management subject matter expertise. During my time working with her she was a “go to” expert for the entire team.',
              author: 'Kate Kalikstein',
              role: 'Business Intelligence Leader',
              initials: 'KK'
            }, {
              quote: 'You are a go-getter. You aim for the stars and you keep thriving to excel beyond expectations. Your Excellent managerial skills, ability to foresee work has helped our team to be at the best.',
              author: 'Asha Vijay',
              role: 'Senior Data Governance Lead, Collibra Solution Architect',
              initials: 'AV'
            }, {
              quote: 'Brigitte is an outstanding facilitator and educator as well as worker. She is motivational and always willing to help to move initiatives forward. Her intelligence and forethought are exceptional. I thank Brigitte for her leadership and ability to motivate our groups in such a professional way.',
              author: 'Miller Debbie',
              role: 'Director of Medical Staff Services at Sisters of Providence Health System',
              initials: 'MD'
            }, {
              quote: 'Expert team builder',
              author: 'Steve Burdick',
              role: 'CEO at Providence Health & Services',
              initials: 'SB'
            }].map(testimonial => <ExpandableTestimonial key={testimonial.author} {...testimonial} variant="about" data-id="element-241" />)}
            </motion.div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-242" />
    </div>;
}